import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { InternalScheduleDialogProps } from './types';
import { useTranslation } from 'react-i18next';
import { useDate } from '@hooks/date';
import { useEffect, useRef, useState } from 'react';
import { useGetUsersSchedules } from '@services/schedules/internal';
import { Form, FormField } from '@components/ui/form';
import { useInternalScheduleForm } from '@forms/schedules/internal';
import { useToast } from '@hooks/useToast';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { Input } from '@components/ui/form/input';
import { Checkbox } from '@components/ui/checkbox';
import { useRemoveInternalSchedule } from '@services/schedules/internal/remove';
import { formatDate } from '@utils/dateHelpers';
import { useSchedulerContext } from '@contexts/scheduler';
import { initialState } from '@contexts/scheduler/constants';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { initialState as sidebarInitialState } from '@contexts/scheduler/sidebar/constants';
import { SchedulerSidebarContextData } from '@contexts/scheduler/sidebar/types';
import Scheduler from '@components/scheduler';
import { useRescheduler } from '@contexts/rescheduler';
import { handleRelatedSchedulesResponse } from '@contexts/rescheduler/helpers';
import { ReschedulerContextData } from '@contexts/rescheduler/types';
import { RemoveScheduleDialog } from '../remove';
import { SchedulerDate } from '@components/scheduler/date';
import { useQueryParams } from '@hooks/queryParams';
import { MultiSelect } from '@components/ui/form/multiSelect';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useSchedulerMaxHeight } from '@hooks/scheduler/maxHeight';
import { SpecialtyOverrideDialog } from './action/specialty';
import { OverlapScheduleDialog } from './action/overlap';
import { InternalScheduleActions } from './actions';
import { SchedulerSidebar } from '@components/scheduler/sidebar';
import { Button } from '@components/ui/button';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { Conflict } from './publish/types';
import { formatTime } from '@utils/i18n';
import {
  getDisplayOptionsSettings,
  getWorkloadDisplayOptionsSettingsEndpoint,
} from '@components/workload/drawer/displayOptions/helpers';
import { useAppContext } from '@contexts/app';
import { cn } from '@utils/cn';

export default function InternalScheduleDialog({
  open,
  setOpen,
  type,
  schedule,
  jobId,
  initialDate,
  activeId,
  readOnly,
  initialParams = {},
}: InternalScheduleDialogProps) {
  const { t } = useTranslation('app');
  const { displayOptions } = useAppContext();
  const { toast } = useToast();
  const { active, ...scheduler } = useSchedulerContext();
  const { setState: setSidebarState } = useSchedulerSidebar() as SchedulerSidebarContextData;
  const rescheduler = useRescheduler();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [overlap, setOverlap] = useState(false);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [specialtyOverride, setSpecialtyOverride] = useState(false);
  const [remove, setRemove] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedForemen, setSelectedForemen] = useState<string[]>(initialParams.users || []);
  const [isInitialized, setIsInitialized] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const maxHeight = useSchedulerMaxHeight(popoverRef);
  const {
    params: { allUsers, type: calendarType },
    setParam,
  } = useQueryParams();

  const { specialties, fieldUsers } = useDropdownsContext();

  const skipSpecialtyConfirmation = getDisplayOptionsSettings(
    displayOptions || [],
    getWorkloadDisplayOptionsSettingsEndpoint(calendarType),
  )?.schedulingSettingsHideMissingSpecialties;

  const { date, rawDate, setRawDate } = useDate({
    initialDate,
    disableLoadFromParams: true,
    disableParamsUpdate: true,
  });

  const { data } = useGetUsersSchedules({
    date: formatDate(date),
    specialties: selectedSpecialties,
    users: selectedForemen,
    active: open && isInitialized,
    crewTypes: ['field_user'],
    divisionIds: [schedule?.task?.job?.division?.id || ''],
    scheduleId: schedule?.id,
  });

  const removeSchedule = useRemoveInternalSchedule({
    id: active.schedule.id,
    taskId: active.schedule.task?.id || '',
    jobId,
    callback: ({ success }) => {
      if (!success) return;
      setOpen(false);
    },
  });

  const { form, onSubmit, locked } = useInternalScheduleForm({
    successHandler: (resp) => {
      const draftSent = schedule?.status === 'draft' && resp?.data?.status !== schedule?.status;
      form.reset();
      setParam('allUsers', '');
      setOpen(false);
      setOverlap(false);
      setSpecialtyOverride(false);
      toast({
        title: t(
          `messages.success.schedule.${draftSent ? 'saveAndSend' : schedule?.status ? 'update' : 'create'}`,
        ),
      });
      if (!resp || !rescheduler) return;
      handleRelatedSchedulesResponse(rescheduler as ReschedulerContextData, resp);
    },
    onOverlap: (conflicts) => {
      setOverlap(true);
      setConflicts(conflicts);
    },
    onSpecialtyOverride: () => setSpecialtyOverride(true),
  });

  const { watch, setValue, resetField } = form;

  useEffect(() => {
    if (!open) {
      setIsInitialized(false);
      return;
    }
    setSelectedSpecialties(type ? [type.value] : []);
    setSelectedForemen(initialParams.users || []);
    if (initialDate) setRawDate(initialDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type]);

  useEffect(() => {
    if (!open || isInitialized) return;

    const specialtiesReady =
      JSON.stringify(selectedSpecialties) === JSON.stringify(type ? [type.value] : []);
    const foremenReady =
      JSON.stringify(selectedForemen) === JSON.stringify(initialParams.users || []);
    const dateReady = !initialDate || formatDate(date) === formatDate(initialDate);

    if (specialtiesReady && foremenReady && dateReady) setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isInitialized, selectedSpecialties, selectedForemen, date]);

  useEffect(() => {
    if (skipSpecialtyConfirmation) {
      setValue('forceSpecialty', skipSpecialtyConfirmation);
      return;
    }
    if (!open || !type?.value) return;
    const arr =
      (active?.schedule?.foreman?.specialties?.length
        ? active?.schedule?.foreman?.specialties?.map((el) => el.value)
        : initialParams.specialties) || [];
    setValue('forceSpecialty', arr.length ? arr.includes(type?.value) : true);
  }, [
    open,
    setValue,
    type?.value,
    active?.schedule?.foreman?.specialties,
    initialParams.specialties,
    skipSpecialtyConfirmation,
  ]);

  useEffect(() => {
    if (!open) return;

    setSidebarState((prev) => ({ ...prev, populateDefaultCrewAndEquipment: true }));

    return () => {
      setSidebarState(sidebarInitialState);
    };
  }, [open, setSidebarState]);

  useEffect(() => {
    if (locked || overlap) return;
    if (!open) {
      resetField('note');
      resetField('status');
      scheduler.setState(initialState);
      setSidebarState(sidebarInitialState);

      return;
    }

    const foremanId = active.schedule.foreman?.id || schedule?.foreman?.id || '';
    setValue('foremanId', foremanId);

    if (!watch('note')) setValue('note', schedule?.task?.note?.content || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialDate,
    open,
    schedule,
    type,
    locked,
    setValue,
    active.schedule.foreman?.id,
    active.schedule.id,
  ]);

  useEffect(() => {
    if (skipSpecialtyConfirmation) {
      setValue('forceSpecialty', skipSpecialtyConfirmation);
      return;
    }
    if (!selectedForemen.length) return;
    const currentForemanId = active.schedule.foreman?.id || schedule?.foreman?.id;
    if (!currentForemanId) return;

    setValue('forceSpecialty', selectedForemen.includes(currentForemanId));
  }, [
    selectedForemen,
    skipSpecialtyConfirmation,
    setValue,
    schedule?.foreman?.id,
    active.schedule.foreman?.id,
  ]);

  useEffect(() => {
    if (schedule?.id === 'new' || !schedule?.id || ['approved', 'draft'].includes(schedule.status))
      setValue('confirmed', true);
  }, [schedule?.id, schedule?.status, schedule?.task?.id, setValue]);

  const schedulerStyle = maxHeight > 0 ? { height: maxHeight } : undefined;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          ref={popoverRef}
          className="w-[calc(100%-3rem)] gap-0 overflow-hidden p-0"
          onEscapeKeyDown={(e) => {
            if (document.activeElement?.hasAttribute('data-revert-on-escape')) e.preventDefault();
          }}>
          <DialogHeader className="scheduler-navigation sticky top-0 z-10 flex flex-row justify-between bg-white p-6 shadow-md">
            <DialogTitle className="h-fit">{t('schedule.team', { team: type?.name })}</DialogTitle>
            <SchedulerDate
              className="absolute left-1/2 top-3.5 -translate-x-1/2"
              date={rawDate}
              setDate={setRawDate}
            />
          </DialogHeader>
          <div
            className="scheduler-container scrollbar-thin mt-6 flex max-h-full min-h-0 items-stretch overflow-y-auto pl-6 pr-0"
            style={schedulerStyle}>
            <div className="h-fit min-h-0 shrink">
              <Scheduler
                modal
                date={rawDate}
                foremen={data?.records}
                withoutTeam={data?.withoutTeam}
                outOfScopeRecords={data?.outOfScopeRecords}
                schedule={schedule}
                activeId={activeId}
                restrictTo="schedulerGrid"
              />
            </div>
            <div
              className={cn(
                'flex min-h-0 grow-0 flex-col sticky top-0 z-10 pt-6',
                sidebarExpanded ? 'shrink-2 basis-[848px]' : 'shrink-0 basis-10',
              )}>
              {sidebarExpanded ? (
                <Button
                  variant="ghost"
                  className="h-fit w-full text-xs shrink-0 justify-start gap-1.5 self-start p-1 text-neutral-700 hover:bg-transparent hover:text-neutral-900"
                  onClick={() => setSidebarExpanded(false)}>
                  <span className="flex shrink-0 items-center text-xs">
                    <SvgConsumer id="arrow" />
                    <SvgConsumer id="arrow" className="-ml-1.5" />
                  </span>
                  {t('actions.hide')}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  className="h-8 w-8 shrink-0 self-start p-0 text-neutral-400 hover:bg-transparent hover:text-neutral-700"
                  onClick={() => setSidebarExpanded(true)}>
                  <SvgConsumer id="arrow" className="-scale-x-100" />
                </Button>
              )}
              {sidebarExpanded && <SchedulerSidebar />}
            </div>
          </div>
          <div ref={popoverRef} className="overflow-visible" />
          <Form {...form}>
            <form
              className="dialog-footer border-t p-6"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                control={form.control}
                name="forceOverlapping"
                render={() => (
                  <FormItemWrapper>
                    <input type="hidden" />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="foremanId"
                render={({ field }) => (
                  <FormItemWrapper>
                    <input type="hidden" {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="startsAt"
                render={({ field }) => (
                  <FormItemWrapper>
                    <input type="hidden" {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="endsAt"
                render={({ field }) => (
                  <FormItemWrapper>
                    <input type="hidden" {...field} />
                  </FormItemWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItemWrapper label={t('forms.note.label')}>
                    <Input disabled={readOnly} {...field} />
                  </FormItemWrapper>
                )}
              />
              <div className="flex flex-col gap-2">
                <div className="mt-6 flex items-start justify-end gap-2">
                  {!readOnly && (
                    <div className="flex h-10 flex-1 items-center justify-between gap-4">
                      <div className="flex gap-4">
                        <MultiSelect
                          placeholder={t('filters.teams.placeholder')}
                          className="w-fit whitespace-nowrap"
                          options={specialties.options.map((el) => ({
                            value: el.value,
                            label: el.name,
                          }))}
                          value={selectedSpecialties}
                          limit={1}
                          onChange={setSelectedSpecialties}
                          disabled={specialties.disabled}
                          side="top"
                          popoverRef={popoverRef}
                        />
                        <MultiSelect
                          placeholder={t('filters.foremen.placeholder')}
                          className="w-fit whitespace-nowrap"
                          options={fieldUsers.options
                            .filter((el) =>
                              selectedForemen.length
                                ? !data?.records.some(
                                    (record) =>
                                      record.id === el.id && !selectedForemen.includes(el.id),
                                  )
                                : true,
                            )
                            .map((el) => ({
                              value: el.id,
                              label: el.fullName || '',
                            }))}
                          value={selectedForemen}
                          limit={1}
                          onChange={setSelectedForemen}
                          disabled={fieldUsers.disabled}
                          side="top"
                          popoverRef={popoverRef}
                        />
                        <Checkbox
                          id="allDivisions"
                          checked={allUsers === 'true'}
                          onCheckedChange={() =>
                            setParam('allUsers', allUsers === 'true' ? '' : 'true')
                          }>
                          {t('schedules.allDivisions')}
                        </Checkbox>
                      </div>
                      <div className="flex flex-grid items-center gap-2">
                        <FormField
                          control={form.control}
                          name="confirmed"
                          render={({ field: { value, onChange } }) => (
                            <FormItemWrapper
                              className={cn('w-fit', schedule?.status === 'draft' && 'mt-7')}>
                              <div className="flex gap-2">
                                <Checkbox
                                  id="confirmed"
                                  checked={Boolean(value)}
                                  onCheckedChange={onChange}>
                                  {t('schedules.markAsConfirmed')}
                                </Checkbox>
                              </div>
                            </FormItemWrapper>
                          )}
                        />
                        <InternalScheduleActions
                          onCancel={() => {
                            form.reset();
                            setOpen(false);
                          }}
                          onRemove={() => setRemove(true)}
                          onSendSchedule={() => {
                            setValue('actionType', 'saveAndSend');
                            void form.handleSubmit(onSubmit)();
                          }}
                          onRescheduleAsDraft={() => {
                            setValue('status', 'draft');
                            setValue('actionType', '');
                            void form.handleSubmit(onSubmit)();
                          }}
                          onSaveAsDraft={() => setValue('actionType', '')}
                          locked={locked || !active.schedule.id}
                          scheduleId={schedule?.id}
                          foremanId={active.schedule.foreman?.id}
                          readOnly={readOnly}
                          isDraft={schedule?.status === 'draft'}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
            {overlap && (
              <OverlapScheduleDialog
                open={overlap}
                setOpen={setOverlap}
                disabled={locked || !active.schedule.id}
                onCancel={() => setValue('forceOverlapping', false)}
                onConfirm={() => {
                  setValue('forceOverlapping', true);
                  void form.handleSubmit(onSubmit)();
                }}
                conflicts={conflicts || []}
                date={
                  active.schedule?.startsAt
                    ? formatDate(new Date(active.schedule.startsAt), 'MMM do')
                    : ''
                }
                name={active.schedule?.task?.name}
                summaryDetail={
                  active.schedule?.startsAt
                    ? `${[
                        formatTime(new Date(active.schedule.startsAt)),
                        formatTime(new Date(active.schedule?.endsAt || '')),
                      ].join(
                        ' - ',
                      )}, ${formatDate(new Date(active.schedule.startsAt), 'MMM do, yyyy')}`
                    : ''
                }
              />
            )}
            <SpecialtyOverrideDialog
              open={specialtyOverride}
              setOpen={setSpecialtyOverride}
              disabled={locked || !active.schedule.id}
              onCancel={() => setValue('forceSpecialty', false)}
              onConfirm={() => {
                setValue('forceSpecialty', true);
                void form.handleSubmit(onSubmit)();
              }}
            />
          </Form>
        </DialogContent>
      </Dialog>
      <RemoveScheduleDialog
        open={remove}
        setOpen={setRemove}
        onConfirm={removeSchedule.mutate}
        disabled={removeSchedule.isLoading}
      />
    </>
  );
}
